import axios from 'axios';
import moment from 'moment';

const formatDate = (dateStr: string | undefined): string => {
  if (dateStr && dateStr !== 'n/a') {
    const dateTime = new Date(dateStr);
    return moment(dateTime).format('MMM DD, YYYY HH:mm');
  }

  return 'n/a';
};

const formatSpecData = (data: SpecData[]): SpecData[] => {
  if (data) {
    return data.reduce((acc: any, curr: SpecData) => {
      const { mainInvoker, output } = curr;

      return [
        ...acc,
        {
          arguments: curr.arguments,
          mainInvoker,
          output,
        },
      ];
    }, []);
  }

  return [];
};

export interface SpecData {
  id?: string;
  arguments: string[];
  mainInvoker: string;
  output: string;
}

export interface SpecInterface {
  assignmentOrderIndex?: number;
  id?: string;
  name?: string;
  autoGradeOff?: boolean;
  showTestCase?: boolean;
  solution?: string;
  env?: string;
  entry?: string;
  data?: SpecData[];
  status?: string;
  assignmentID?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

class Spec {
  public assignmentOrderIndex?: number;
  public id: string = '';
  public name: string = '';
  public autoGradeOff: boolean = false;
  public showTestCase?: boolean = false;
  public solution: string = '';
  public env: string = 'node';
  public entry: string = 'argv';
  public data: SpecData[] = [];
  public status?: string;
  public assignmentID?: string;
  public createdAt?: string;
  public updatedAt?: string;
  public deletedAt?: string;

  constructor(props?: Spec | SpecInterface) {
    if (props) {
      this.id = props.id || this.id;
      this.name = props.name || this.name;
      this.autoGradeOff = props.autoGradeOff || this.autoGradeOff;
      this.showTestCase = props.showTestCase || this.showTestCase;
      this.solution = props.solution || this.solution;
      this.env = props.env || this.env;
      this.entry = props.entry || this.entry;
      this.data = props.data || this.data;
      this.assignmentOrderIndex = props.assignmentOrderIndex;
      this.assignmentID = props.assignmentID;

      if (props.createdAt) {
        this.createdAt = formatDate(props.createdAt);
      }

      if (props.updatedAt) {
        this.updatedAt = formatDate(props.updatedAt);
      }

      if (props.deletedAt) {
        this.deletedAt = formatDate(props.deletedAt);
      }
    }
  }

  public static async find(query?: { assignmentID: string }): Promise<Spec[]> {
    const { data = [] } = await axios.get('/specs', {
      params: query
    });
    const sortedData: Spec[] = (data || []);
    sortedData.sort((a, b) => {
      if (a.assignmentOrderIndex === undefined)
        return 1;

      if (b.assignmentOrderIndex === undefined)
        return -1;

      if (a.assignmentOrderIndex > b.assignmentOrderIndex)
        return 1;

      if (a.assignmentOrderIndex < b.assignmentOrderIndex)
        return -1;

      return 0;
    });
    return sortedData;
  }

  public static async findById(id: string): Promise<Spec> {
    const { data } = await axios.get<SpecInterface>('/admin/spec', {
      params: { id }
    });
    return new Spec(data);
  }

  public static async fetchList(): Promise<Spec[]> {
    const { data } = await axios.get<SpecInterface[]>('/admin/specs');
    return data.map(s => new Spec(s));
  }

  public static async delete(id: string) {
    const { data } = await axios.delete('/admin/spec', {
      params: { id }
    });
    return data;
  }

  public static getTestCaseMainInvoker(testCase: Spec['data'][number]) {
    return `main(${testCase.arguments.map((arg) => (Number(arg) ? Number(arg) : `"${arg}"`))})`;
  }

  public async save() {
    let {
      id,
      name,
      autoGradeOff,
      showTestCase,
      entry,
      env,
      data,
      assignmentID,
      assignmentOrderIndex,
    } = this;
    if (!name) {
      name = id.replace(/-/g, ' ');
    }

    const { data: result } = await axios.post('admin/spec', {
      id,
      name,
      autoGradeOff,
      showTestCase,
      entry,
      env,
      data,
      assignmentID,
      assignmentOrderIndex,
    });

    return result;
  }

  public async update() {
    let {
      id,
      name,
      autoGradeOff,
      showTestCase,
      entry,
      env,
      data,
      assignmentID,
      assignmentOrderIndex,
    } = this;
    if (!name) {
      name = id.replace(/-/g, ' ');
    }

    const { data: result } = await axios.put('admin/spec', {
      id,
      name,
      autoGradeOff,
      showTestCase,
      entry,
      env,
      data,
      assignmentID,
      assignmentOrderIndex,
    });

    return result;
  }

  public formatData() {
    this.data = formatSpecData(this.data);
  }

  public addItem(item?: SpecData) {
    this.data.push(
      item || {
        arguments: [],
        mainInvoker: 'main()',
        output: '',
      }
    );
  }
}

export default Spec;
